import { useCallback, useMemo } from 'react';
import MergeCheckbox from '../MergeCheckbox';
import CellComponent from '../CellComponent';

import useTexts from './useTexts';

const FIELD_NAMES = {
  ID: 'id',
  SR_TYPE: 'srType',
  DESCRIPTION: 'description',
  TIME: 'insertTime',
  TITLE: 'title',
  STATUS: 'status',
  REQUEST_USER: 'requestUser',
  ASSIGNEE: 'assignee',
};

export const mergeTableColumnConfigs = [
  {
    fieldName: FIELD_NAMES.ID,
    displayName: 'spaces.template.fields.id',
    alwaysReadOnly: true,
    id: 2,
    sysaidEntity: null,
    customColumn: false,
    ticketTemplateFieldType: {
      id: 3,
      typeName: 'number',
    },
  },
  {
    fieldName: FIELD_NAMES.SR_TYPE,
    displayName: 'spaces.template.fields.srType',
    alwaysReadOnly: true,
    id: 32,
    sysaidEntity: null,
    customColumn: false,
    ticketTemplateFieldType: {
      id: 6,
      typeName: 'select',
    },
  },
  {
    fieldName: FIELD_NAMES.DESCRIPTION,
    displayName: 'spaces.template.fields.description',
    alwaysReadOnly: false,
    id: 5,
    sysaidEntity: null,
    customColumn: false,
    ticketTemplateFieldType: {
      id: 2,
      typeName: 'longtext',
    },
  },
  {
    fieldName: FIELD_NAMES.TIME,
    displayName: 'spaces.template.fields.insertTime',
    alwaysReadOnly: true,
    id: 10,
    sysaidEntity: null,
    customColumn: false,
    ticketTemplateFieldType: {
      id: 11,
      typeName: 'dateAndTime',
    },
  },
  {
    fieldName: FIELD_NAMES.TITLE,
    displayName: 'spaces.template.fields.title',
    alwaysReadOnly: false,
    id: 57,
    sysaidEntity: null,
    customColumn: false,
    ticketTemplateFieldType: {
      id: 1,
      typeName: 'text',
    },
  },
  {
    fieldName: FIELD_NAMES.STATUS,
    displayName: 'spaces.template.fields.status',
    alwaysReadOnly: false,
    id: 8,
    sysaidEntity: 'customValues',
    customColumn: false,
    ticketTemplateFieldType: {
      id: 6,
      typeName: 'select',
    },
  },
  {
    fieldName: FIELD_NAMES.REQUEST_USER,
    displayName: 'spaces.template.fields.requestUser',
    alwaysReadOnly: false,
    id: 16,
    sysaidEntity: 'users',
    customColumn: false,
    ticketTemplateFieldType: {
      id: 6,
      typeName: 'select',
    },
  },
  {
    fieldName: FIELD_NAMES.ASSIGNEE,
    displayName: 'spaces.template.fields.assignee',
    alwaysReadOnly: false,
    id: 62,
    sysaidEntity: 'users',
    customColumn: false,
    ticketTemplateFieldType: {
      id: 6,
      typeName: 'select',
    },
  },
];

const CONFIGS_BY_NAME = {
  [FIELD_NAMES.ID]: {
    width: 90,
    minWidth: 68,
  },
  [FIELD_NAMES.SR_TYPE]: {
    width: 100,
    minWidth: 100,
  },
  [FIELD_NAMES.DESCRIPTION]: {
    width: 270,
    minWidth: 270,
  },
  [FIELD_NAMES.TIME]: {
    width: 181,
    minWidth: 181,
  },
  [FIELD_NAMES.TITLE]: {
    width: 120,
    minWidth: 100,
  },
  [FIELD_NAMES.STATUS]: {
    width: 120,
    minWidth: 120,
  },
  [FIELD_NAMES.REQUEST_USER]: {
    width: 180,
    minWidth: 180,
  },
  [FIELD_NAMES.ASSIGNEE]: {
    width: 150,
    minWidth: 150,
  },
};

const checkBoxColumn = {
  field: 'checkbox',
  headerName: '',
  headerCheckboxSelection: false,
  checkboxSelection: false,
  width: 64,
  minWidth: 64,
  cellRenderer: (params) => <MergeCheckbox {...params} />,
};

const useMergeTableColumns = () => {
  const { getColumnText } = useTexts();

  const handleCellRenderer = useCallback((props) => <CellComponent {...props} />, []);

  const columns = useMemo(
    () =>
      mergeTableColumnConfigs.map(
        ({ fieldName: name, id, ticketTemplateFieldType, sysaidEntity, displayName, customColumn }, index) => {
          const headerFromRB = getColumnText && getColumnText(displayName);
          const headerName = headerFromRB || displayName;

          const column = {
            field: name,
            fieldId: id,
            sysaidEntity,
            fieldTypeId: ticketTemplateFieldType?.id,
            fieldTypeName: ticketTemplateFieldType?.typeName?.toUpperCase(),
            suppressMenu: true,
            headerClass: `${name}HeaderClass`,
            headerName,
            customColumn,
            keyString: 'valueKey',
            captionString: 'valueCaption',
            cellRenderer: handleCellRenderer,
            ...(CONFIGS_BY_NAME?.[name] || {}),
            ...(index === mergeTableColumnConfigs.length - 1
              ? {
                  flex: 1,
                }
              : {}),
          };
          return column;
        },
      ),
    [getColumnText, handleCellRenderer],
  );

  const allColumns = useMemo(() => [checkBoxColumn, ...columns], [columns]);

  return allColumns;
};

export default useMergeTableColumns;
